import {useMutation} from '@github-ui/react-query'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {repoTaskSessionsApiPath} from '../utils/api-url-helpers'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {extractImageUrls} from '@github-ui/copilot-shared-ui/composer/markdown-images'
import {activationTraceIdFromError, recordActivationTraceId} from '../ahp/activation-trace'

export interface CreateAPISessionParams {
  nwo: string
  taskId: string
  problemStatement: string
  model?: string
  /** Maps to the CMC OpenAPI `reasoning_effort` request field. */
  reasoningEffort?: ReasoningEffort
  agentId?: number
  customAgent?: string
  eventType: string
}

interface UseCreateAPISessionOptions {
  onError?: (error: Error) => void
  onSuccess?: () => void
}

export function useCreateAPISession({onError, onSuccess}: UseCreateAPISessionOptions = {}) {
  const createSessionMutation = useMutation<void, Error, CreateAPISessionParams>({
    mutationKey: ['createAPISession'],
    mutationFn: async (params: CreateAPISessionParams): Promise<void> => {
      const imageCount = extractImageUrls(params.problemStatement).length

      sendEvent('copilot.agent_session.session_creation_requested', {
        nwo: params.nwo,
        task_id: params.taskId,
        image_count: imageCount,
      })

      try {
        await createSession(params)
      } catch (error) {
        recordActivationTraceId(params.taskId, activationTraceIdFromError(error))
        if (error instanceof FetchCAPIError) {
          sendEvent('copilot.agent_session.session_creation_failed', {
            nwo: params.nwo,
            task_id: params.taskId,
            status: error.status,
            error: error.message,
            image_count: imageCount,
          })
        }

        throw error
      }

      sendEvent('copilot.agent_session.session_creation_accepted', {
        nwo: params.nwo,
        task_id: params.taskId,
        image_count: imageCount,
      })
    },
    onError: error => {
      onError?.(error)
    },
    onSuccess: () => {
      onSuccess?.()
    },
  })

  const createAPISession = (params: CreateAPISessionParams) => {
    createSessionMutation.mutate(params)
  }

  return {
    createSession: createAPISession,
    isCreating: createSessionMutation.isPending,
    createError: createSessionMutation.error,
  }
}

/**
 * Helper function to create session via the Task API
 */
async function createSession({
  nwo,
  taskId,
  problemStatement,
  model,
  reasoningEffort,
  agentId,
  customAgent,
  eventType,
}: CreateAPISessionParams): Promise<Response> {
  const endpoint = repoTaskSessionsApiPath(nwo, taskId)

  return await fetchCAPI({
    path: endpoint,
    method: 'POST',
    body: {
      problem_statement: problemStatement,
      event_type: eventType,
      ...(model && {model}),
      ...(reasoningEffort && {reasoning_effort: reasoningEffort}),
      ...(agentId && {agent_id: agentId}),
      ...(customAgent && {custom_agent: customAgent}),
    },
  })
}
